import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Heart, Minus, Plus, Truck, ShieldCheck } from "lucide-react";
import { api, money } from "@/lib/api";
import { KiddeeSeal } from "@/components/KiddeeSeal";
import { ProductJsonLd } from "@/components/ProductJsonLd";
import { ProductGallery } from "@/components/ProductGallery";
import { ProductReviews } from "@/components/ProductReviews";
import { ProductSeo, ProductDetails, RelatedProducts } from "@/components/ProductExtras";
import { track } from "@/lib/track";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import { PRODUCT } from "@/constants/testIds";
import { useInView } from "@/lib/useInView";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { ProductArticles } from "@/components/ProductArticles";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(undefined);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const { slugs, toggle } = useFavorites();
  const [buyRef, buyInView] = useInView();

  useEffect(() => {
    setProduct(undefined);
    setQty(1);
    api.get(`/products/${slug}`).then(({ data }) => { setProduct(data); track("product_view", { slug: data.slug, value: data.price }); }).catch(() => setProduct(null));
  }, [slug]);

  if (product === null)
    return (
      <main data-testid={PRODUCT.notFound} className="pt-32 pb-10 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-heading text-3xl font-bold">We couldn't find that product.</h1>
        <Link to="/shop" className="mt-6 inline-block underline">Back to the shop</Link>
      </main>
    );
  if (!product) return <main className="pt-32 min-h-[60vh]" />;
  const fav = slugs.includes(product.slug);

  return (
    <main data-testid={PRODUCT.page} className="pt-24 sm:pt-28 pb-28 md:pb-10 max-w-6xl mx-auto px-5 sm:px-6 lg:px-10">
      <ProductJsonLd product={product} />
      <ProductSeo product={product} />
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Link to="/shop" className="min-h-[44px] inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#E06D44] transition-colors"><ArrowLeft size={16} /> Shop</Link>
        {(product.kiddee_fav || product.category === "toys") && (
          <Link to="/shop?cat=kiddee" data-testid={PRODUCT.backToKiddee} className="min-h-[44px] inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#E06D44] transition-colors">Kiddee collection</Link>
        )}
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-10 lg:gap-16 items-start fade-up">
        <div className="relative">
          <ProductGallery product={product} />
          {product.kiddee_fav && <KiddeeSeal testId={PRODUCT.kiddeeSeal} className="absolute top-5 left-5" />}
        </div>
        <div className="min-w-0">
          {product.kiddee_fav && <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D44]">Kiddee pick</span>}
          <h1 data-testid={PRODUCT.name} className="font-heading mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">{product.name}</h1>
          <p data-testid={PRODUCT.price} className="mt-4 text-2xl font-semibold">{money(product.price)}</p>
          {product.verdict && (
            <figure data-testid={PRODUCT.verdict} className="mt-6 flex gap-3 items-start bg-[#FDF2EC] rounded-2xl p-4">
              <img src="/media/kiddee/2.jpg" alt="Kiddee" className="w-11 h-11 rounded-full object-cover shrink-0" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D44]">Kiddee's verdict</p>
                <blockquote className="mt-1 font-heading font-semibold">"{product.verdict}"</blockquote>
              </div>
            </figure>
          )}
          <ProductArticles slug={product.slug} />
          <p className="mt-6 text-stone-600 leading-relaxed">{product.description}</p>
          <div ref={buyRef} className="mt-8 flex items-center gap-3">
            <div className="flex items-center border border-[#E7E0D3] rounded-full">
              <button data-testid={PRODUCT.qtyMinus} aria-label="Less" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-11 grid place-items-center"><Minus size={16} /></button>
              <span data-testid={PRODUCT.qtyInput} className="w-8 text-center font-medium">{qty}</span>
              <button data-testid={PRODUCT.qtyPlus} aria-label="More" onClick={() => setQty((q) => Math.min(50, q + 1))} className="w-11 h-11 grid place-items-center"><Plus size={16} /></button>
            </div>
            <button data-testid={PRODUCT.addButton} onClick={() => add(product, qty)} className="flex-1 bg-[#E06D44] text-white py-3.5 rounded-full font-medium hover:bg-[#C95B33] transition-colors">
              Add to cart
            </button>
            <button data-testid={PRODUCT.favButton} aria-label="Save to favorites" onClick={() => toggle(product)} className="w-12 h-12 rounded-full border border-[#E7E0D3] grid place-items-center hover:bg-[#F0EBE1] transition-colors">
              <Heart size={18} className={fav ? "fill-[#E11D48] text-[#E11D48]" : ""} />
            </button>
          </div>
          <ul className="mt-8 space-y-3 text-sm text-stone-600">
            <li className="flex items-center gap-3"><Truck size={18} className="text-[#4E7B68]" /> Free shipping over $50 · ships in 1–2 days</li>
            <li className="flex items-center gap-3"><ShieldCheck size={18} className="text-[#4E7B68]" /> Happy pets or money back within 30 days</li>
          </ul>
          <ProductDetails product={product} />
        </div>
      </div>
      <ProductReviews slug={product.slug} />
      <RelatedProducts product={product} />
      <MobileStickyBar show={!buyInView} testId={PRODUCT.stickyBar} label={product.name} value={money(product.price * qty)} action={qty > 1 ? `Add ${qty} to cart` : "Add to cart"} onAction={() => add(product, qty)} />
    </main>
  );
}
